import {
  Box,
  Flex,
  Square,
  Text,
  useColorModeValue,
  Tooltip,
  Input,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import TimelineDot from './TimelineDot';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { useEventHistory } from 'src/hooks/event-history';
import {
  EventResource,
  EventType,
  HistoryEventGroupDeepFragment,
} from 'src/gql/graphql';

interface CurrentVersionHeaderProps {
  label?: string;
  isSelected?: boolean;
  isInPreview?: boolean;
  isFirstInSelection?: boolean;
  isFirstInPreview?: boolean;
  isHovered?: boolean;
  onClick?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  projectId: string;
  isNextEventCheckpoint: boolean;
  reloadEventGroups: () => Promise<HistoryEventGroupDeepFragment[]>;
}

// eslint-disable-next-line complexity
export const CurrentVersionHeader: React.FC<CurrentVersionHeaderProps> = ({
  label = 'Current version',
  isSelected = false,
  isInPreview = false,
  isFirstInSelection = false,
  isFirstInPreview = false,
  isHovered = false,
  projectId,
  isNextEventCheckpoint,
  onClick,
  onMouseDown,
  reloadEventGroups,
}) => {
  const [isAddingCheckpoint, setIsAddingCheckpoint] = useState(false);
  const [checkPointName, setCheckpointName] = useState('');
  const currentVersionColor = useColorModeValue('cyan.500', 'cyan.200');
  const selectedBgColor = useColorModeValue('blue.50', 'blue.800');
  const previewBgColor = useColorModeValue('gray.50', 'gray.700');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const selectedBorderColor = useColorModeValue('blue.500', 'blue.300');

  const { t } = useTranslation('project-history');
  const { logEvent } = useEventHistory(projectId);

  const handleCheckPointCreation = useCallback(async () => {
    setIsAddingCheckpoint(false);
    invariant(checkPointName, 'Check point name is missing');
    await logEvent({
      eventName: checkPointName,
      eventType: EventType.Created,
      // should we add a resource type of checkpoint? or can this can mean that the checkpoint is defined on the project?
      resourceType: EventResource.Project,
      resourceName: checkPointName,
      isCheckpoint: true,
      metadata: {
        checkPointName,
      },
    });
    await reloadEventGroups();
  }, [logEvent, checkPointName]);

  // Calculate background color based on state
  let bgColor;
  if (isInPreview) {
    bgColor = previewBgColor;
  } else if (isSelected) {
    bgColor = selectedBgColor;
  }

  // Calculate border styles - always rounded on top when selected/preview
  // and the bottom is flush with the first event (no radius, no visible border)
  const borderTopRadius = 'lg';
  const borderTop =
    (isSelected && isFirstInSelection) || (isInPreview && isFirstInPreview)
      ? '2px solid'
      : 'none';
  const borderBottom = 'none';

  return (
    <Flex
      position="relative"
      onClick={onClick}
      onMouseDown={onMouseDown}
      cursor={onClick ? 'pointer' : 'default'}
      role="group"
    >
      {/* Current Version dot */}
      <Box
        position="absolute"
        left="17px"
        top={3}
        transform="translateX(-50%)"
        zIndex={2}
      >
        <TimelineDot isCurrentVersion />
      </Box>
      {!isNextEventCheckpoint ? (
        <Tooltip label={t('tooltip.add_checkpoint')}>
          <Box
            position="absolute"
            left="17px"
            cursor="pointer"
            top={3}
            transform="translateX(-50%)"
            zIndex={3}
            visibility={isAddingCheckpoint ? 'visible' : 'hidden'}
            opacity={isAddingCheckpoint ? 1 : 0}
            transition="opacity 0.2s"
            _groupHover={{ visibility: 'visible', opacity: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              setIsAddingCheckpoint(true);
            }}
          >
            <Square
              size="20px"
              color={currentVersionColor}
              backgroundColor={previewBgColor}
              border={isSelected ? '2px solid' : '1px solid'}
              style={{ rotate: '45deg' }}
            >
              <SparkelIcon
                icon={faPlus}
                color="inherit"
                size="xs"
                style={{ rotate: '-45deg' }}
              />
            </Square>
          </Box>
        </Tooltip>
      ) : null}

      {/* Current Version content */}
      <Box
        width="100%"
        paddingLeft={9}
        paddingRight={4}
        backgroundColor={
          isHovered && !isSelected && !isInPreview ? hoverBgColor : bgColor
        }
        borderTopRadius={borderTopRadius}
        borderBottomRadius={0}
        borderTop={borderTop}
        borderBottom={borderBottom}
        paddingTop={borderTop === '2px solid' ? '7px' : '9px'}
        borderColor={selectedBorderColor}
        marginBottom={0}
        _hover={
          !isSelected && onClick && !isHovered
            ? { backgroundColor: hoverBgColor, borderTopRadius: 'lg' }
            : undefined
        }
        transition="background-color 0.2s"
      >
        {isAddingCheckpoint ? (
          <Box>
            <Input
              aria-label="Add checkpoint"
              type="text"
              textAlign={'start'}
              size="sm"
              px={1}
              borderRadius={0}
              borderColor="chakra-border-color"
              autoFocus={true}
              onFocus={(e) => e.target.select()}
              placeholder={t('rename.checkpoint_place_holder')}
              value={checkPointName}
              onChange={(e) => {
                setCheckpointName(e.target.value);
              }}
              onBlur={handleCheckPointCreation}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCheckPointCreation();
                } else if (e.key === 'Escape') {
                  setIsAddingCheckpoint(false);
                  setCheckpointName('');
                }
              }}
            />
          </Box>
        ) : (
          <Text fontWeight="bold" fontSize="sm" color={currentVersionColor}>
            {label}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

export default CurrentVersionHeader;
